/**
 * useChecklistModule - Fonte única da flag do módulo Checklist
 *
 * Lê `checklist_enabled` via RPC `get_checklist_module_settings` e expõe a
 * mutation de ativação (somente administradores, validado no banco).
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useChecklistModule');

/** Hook completo (flag + ativação) */
export function useChecklistModule() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['checklist-module', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_checklist_module_settings', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao ler flag do módulo Checklist', { error: error.message });
        throw error;
      }
      return Boolean((data as { checklist_enabled?: boolean } | null)?.checklist_enabled);
    },
  });

  const toggleModule = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await (supabase.rpc as any)('update_checklist_module_settings', {
        p_enabled: enabled,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['checklist-module'] });
      toast.success(enabled ? 'Módulo Checklist habilitado' : 'Módulo Checklist desabilitado');
    },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível alterar o módulo'),
  });

  return { isEnabled: query.data ?? false, isLoading: query.isLoading, toggleModule };
}

/** Atalho usado pelos gates de navegação */
export function useChecklistModuleEnabled(): boolean {
  return useChecklistModule().isEnabled;
}
